/**
 * useDealImportJobsLookup
 * ------------------------
 * Hook que carrega as importações de negócios (deal_import_jobs) do tenant
 * via RPC `get_deal_import_jobs_lookup`. Usado para popular o sub-dropdown
 * "Lista de Importação" dentro do filtro "Origem do Cadastro" na tela de
 * listagem de Negócios.
 *
 * RELEASE: 2026-05-16 v1.0.0 - Criação inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/** Linha retornada pela RPC `get_deal_import_jobs_lookup`. */
export interface DealImportJobLookup {
  id: string;
  name: string | null;
  source_file_name: string | null;
  status: string | null;
  succeeded_items: number | null;
  created_at: string;
}

interface Options {
  /** Termo de busca opcional (server-side, ILIKE em name/source_file_name). */
  search?: string;
  /** Limite (max 500, default 200). */
  limit?: number;
  /** Habilita a query (default true). */
  enabled?: boolean;
}

/**
 * Lista as importações mais recentes do tenant (TOP N por data desc).
 * Cache de 5 minutos — a lista varia pouco durante a navegação.
 */
export function useDealImportJobsLookup(opts: Options = {}) {
  const { currentTenant } = useAuth();
  const { search, limit = 200, enabled = true } = opts;

  return useQuery({
    queryKey: ['deal-import-jobs-lookup', currentTenant?.id, search, limit],
    enabled: enabled && !!currentTenant?.id,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<DealImportJobLookup[]> => {
      const { data, error } = await supabase.rpc('get_deal_import_jobs_lookup', {
        p_tenant_hint: currentTenant?.id || null,
        p_search: search || null,
        p_limit: limit,
      });
      if (error) throw error;
      return (data || []) as DealImportJobLookup[];
    },
  });
}
